import React, { FC, useEffect } from 'react';
import { BasicProps } from 'antd/lib/layout/layout';
import { Col, Row } from 'antd';
import { Header } from 'components/Layout/components/header/Header';
import { LogoutModal } from 'components/Modal/LogoutModal';
import { SyncModal } from 'components/Modal/SyncModal';
import { useOnlineStatus } from 'hooks/useOnlineStatus';
import { StyledLayout } from './components/styled/Layout';
import { StyledContent } from './components/styled/Content';
import { StyledFooter } from './components/styled/Footer';
import { StyledCol } from './components/styled/Col';
import { StyledContentWrapper } from './components/styled/ContentWrapper';
import { RESPONSIVE_SPACINGS_CONFIG } from './const';
import { useLogoutModalVisibilityStore, useSettingsModalVisibilityStore, useSyncModalStore } from '../Modal/store';
import { SettingsModal } from '../Modal/settingsModal';

export interface LayoutProps extends BasicProps {
  footerContent?: React.ReactNode;
  headerContent?: React.ReactNode;
  progressBarContent?: React.ReactNode;
}

export const Layout: FC<LayoutProps> = ({ headerContent, footerContent, progressBarContent, children }) => {
  const { isOnline } = useOnlineStatus();
  const { isVisible: isSyncModalVisible } = useSyncModalStore();
  const { isVisible: isLogoutModalVisible } = useLogoutModalVisibilityStore();
  const { isVisible: isSettingsModalVisible } = useSettingsModalVisibilityStore();

  useEffect(() => {
    // Dynamically set the viewport height according to the used browser
    const setViewportHeight = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    // Call the function on load and resize
    window.addEventListener('resize', setViewportHeight);
    window.addEventListener('load', setViewportHeight);

    // Remove the event listeners on cleanup
    return () => {
      window.removeEventListener('resize', setViewportHeight);
      window.removeEventListener('load', setViewportHeight);
    };
  });

  return (
    <StyledLayout>
      <Row justify="center" style={{ width: '100%', zIndex: 10 }}>
        <Col {...RESPONSIVE_SPACINGS_CONFIG} className="w-100">
          <>
            <Header subtitle={headerContent} />
            {isSyncModalVisible && <SyncModal />}
            {isLogoutModalVisible && <LogoutModal />}
            {isSettingsModalVisible && <SettingsModal />}
          </>
        </Col>
      </Row>

      <Row align="stretch" justify="center" style={{ width: '100%', height: '100%', overflow: 'auto' }}>
        <Col {...RESPONSIVE_SPACINGS_CONFIG} style={{ height: '100%', width: '100%' }}>
          <StyledContent $isOnline={isOnline}>
            <StyledContentWrapper>{children}</StyledContentWrapper>
          </StyledContent>
        </Col>
      </Row>

      {progressBarContent && (
        <Row className="w-100" justify="center">
          <StyledCol {...RESPONSIVE_SPACINGS_CONFIG}>{progressBarContent}</StyledCol>
        </Row>
      )}

      <Row className="w-100" justify="center">
        <Col {...RESPONSIVE_SPACINGS_CONFIG} className="w-100">
          <StyledFooter>{footerContent}</StyledFooter>
        </Col>
      </Row>
    </StyledLayout>
  );
};
